import { User } from "./authService";

/**
 * Coherent Permission Structure
 * Organized by resource type with consistent action patterns
 */
export interface PermissionStructure {
  // Data Resources - Standard CRUD Operations
  tasks: {
    create: boolean; // Create tasks, groups, milestones
    read: boolean; // View tasks, search, filter
    update: boolean; // Edit task properties, reorder, move
    delete: boolean; // Delete tasks and groups
  };

  documents: {
    create: boolean; // Create new documents
    read: boolean; // View document content
    update: boolean; // Edit document content and properties
    delete: boolean; // Delete documents
  };

  calendar: {
    create: boolean; // Create calendar events
    read: boolean; // View calendar events
    update: boolean; // Edit events (drag, resize, properties)
    delete: boolean; // Delete calendar events
  };

  users: {
    create: boolean; // Create user accounts
    read: boolean; // View user accounts and permissions
    update: boolean; // Edit user details and permissions
    delete: boolean; // Delete user accounts
  };

  agents: {
    execute: boolean; // Start, stop, rerun, cancel, or roll back agent work
  };

  feedback: {
    read: boolean;
    create: boolean;
    updateOwn: boolean;
    resolve: boolean;
    manage: boolean;
    export: boolean;
  };

  // View Access - Simple access control
  views: {
    dashboard: boolean; // Access Dashboard view
    kanban: boolean; // Access Kanban view
    gantt: boolean; // Access Gantt view
    calendar: boolean; // Access Calendar view
    docs: boolean; // Access Documents view
    reports: boolean; // Access Reports view
    agentOffice: boolean; // Access Agent Office view
    upgrade: boolean; // Show the Upgrade entry point
    settings: boolean; // Access Settings view
    admin: boolean; // Access Admin panel
  };

  // System Functions - Specific operations
  system: {
    import: boolean; // Import data from external sources
    export: boolean; // Export data to various formats
    history: boolean; // Access history, undo/redo operations
    search: boolean; // Global search functionality
    customize: boolean; // UI customization (theme, layout)
  };
}

/**
 * Function-to-Permission Mapping
 * Maps each of the 147+ functions to their required permission
 */
export const FUNCTION_PERMISSION_MAP: Record<string, string> = {
  // CREATE Functions → *.create
  createNode: "tasks.create",
  quickAddTaskKanban: "tasks.create",
  quickAddTaskGantt: "tasks.create",
  createCalendarEvent: "calendar.create",
  inlineEventCreation: "calendar.create",
  createDocument: "documents.create",
  createUser: "users.create",
  createDependency: "tasks.create",
  createChecklist: "tasks.create",
  createCustomField: "tasks.create",
  createKanbanColumn: "tasks.create",

  // READ Functions → *.read
  getRenderableNodes: "tasks.read",
  viewDocumentContent: "documents.read",
  viewCalendarEvents: "calendar.read",
  viewUsers: "users.read",
  viewDashboard: "dashboard.access",
  globalSearch: "system.search",
  viewTaskDetails: "tasks.read",
  viewReports: "reports.access",
  viewHistory: "system.history",

  // UPDATE Functions → *.update
  updateNode: "tasks.update",
  inlineEditKanban: "tasks.update",
  inlineEditGantt: "tasks.update",
  editDocumentContent: "documents.update",
  editDocumentTitle: "documents.update",
  dragCalendarEvents: "calendar.update",
  resizeCalendarEvents: "calendar.update",
  editUser: "users.update",
  updateUserPermissions: "users.update",
  editKanbanColumn: "tasks.update",
  dragTasksBetweenColumns: "tasks.update",
  dragTaskTimeline: "tasks.update",
  reorderTasks: "tasks.update",
  updateProgress: "tasks.update",
  toggleDarkMode: "system.customize",
  adjustFontScale: "system.customize",
  reorderMenuItems: "system.customize",

  // DELETE Functions → *.delete
  deleteNode: "tasks.delete",
  bulkDeleteTasks: "tasks.delete",
  deleteDocument: "documents.delete",
  deleteUser: "users.delete",
  deleteKanbanColumn: "tasks.delete",
  contextMenuDelete: "tasks.delete",
  deleteDependency: "tasks.delete",
  deleteChecklist: "tasks.delete",

  // SPECIAL Functions → system.*
  exportJSON: "system.export",
  exportCSV: "system.export",
  exportMarkdown: "system.export",
  importJSON: "system.import",
  importTeamGantt: "system.import",
  undoRedo: "system.history",
  searchPanel: "system.search",
  uiCustomization: "system.customize",

  // VIEW ACCESS → access.*
  accessDashboard: "dashboard.access",
  accessKanban: "kanban.access",
  accessGantt: "gantt.access",
  accessCalendar: "calendar.access",
  accessDocs: "docs.access",
  accessReports: "reports.access",
  accessAgentOffice: "agent_office.access",
  accessUpgrade: "upgrade.access",
  accessSettings: "settings.access",
  accessAdmin: "admin.access",
};

/**
 * Centralized Permission Service
 * Single source of truth for all permission checks
 */
export class PermissionService {
  private user: User | null = null;

  constructor(user: User | null) {
    this.user = user;
  }

  // Internal helpers for tolerant permission maps (boolean | null | undefined)
  private get userPerms(): Record<string, boolean | null | undefined> {
    // JWT may contain only true flags; UI may use tri-state null; be tolerant
    return (this.user?.permissions as Record<string, boolean | null | undefined>) || {};
  }

  private get rolePerms(): Record<string, boolean | null | undefined> {
    // rolePermissions may be provided in some responses; optional
    const userRecord = this.user as unknown as Record<string, unknown> | null;
    return (userRecord?.rolePermissions as Record<string, boolean | null | undefined>) || {};
  }

  private get hasAuthoritativePermissionPayload(): boolean {
    return Boolean(this.user && Object.prototype.hasOwnProperty.call(this.user, "permissions"));
  }

  private hasFlatPermission(key: string): boolean {
    if (this.hasAuthoritativePermissionPayload) {
      return this.userPerms[key] === true;
    }
    return this.rolePerms[key] === true;
  }

  private hasAnyProjectScope(): boolean {
    return Array.isArray(this.user?.projects) && this.user.projects.length > 0;
  }

  private projectScopeAllowsView(viewName: string): boolean {
    const scopes = this.user?.projects || [];
    if (!scopes.length) return false;

    for (const scope of scopes) {
      const viewPermissions =
        scope.view_permissions ||
        scope.viewPermissions;
      if (viewPermissions?.[viewName] === true) {
        return true;
      }

      const availableViews =
        scope.available_views ||
        scope.availableViews ||
        [];
      if (Array.isArray(availableViews) && availableViews.includes(viewName)) {
        return true;
      }
    }

    return false;
  }

  private projectScopeHasAuthoritativeViewPolicy(viewName: string): boolean {
    const scopes = this.user?.projects || [];
    if (!scopes.length) return false;

    for (const scope of scopes) {
      const viewPermissions =
        scope.view_permissions ||
        scope.viewPermissions;
      if (
        viewPermissions &&
        Object.prototype.hasOwnProperty.call(viewPermissions, viewName)
      ) {
        return true;
      }

      const availableViews =
        scope.available_views ||
        scope.availableViews;
      if (Array.isArray(availableViews)) {
        return true;
      }
    }

    return false;
  }

  private hasProjectScopedViewAccess(viewName: string): boolean {
    if (!this.hasAnyProjectScope()) return false;
    if (this.projectScopeAllowsView(viewName)) return true;
    if (this.projectScopeHasAuthoritativeViewPolicy(viewName)) return false;

    switch (viewName) {
      case "dashboard":
        return false;
      case "gantt":
      case "kanban":
        return this.hasFlatPermission("tasks.read");
      case "calendar":
        return this.hasFlatPermission("calendar.read");
      case "docs":
        return this.hasFlatPermission("documents.read");
      default:
        return false;
    }
  }

  /**
   * Update the current user
   */
  setUser(user: User | null): void {
    this.user = user;
  }

  /**
   * Check if user has a specific permission
   * @param resource - The resource (e.g., 'tasks', 'documents')
   * @param action - The action (e.g., 'create', 'read', 'update', 'delete')
   * @param view - Optional view context (e.g., 'gantt', 'kanban')
   * @returns boolean - Whether user has permission
   */
  hasPermission(
    resource: string,
    action: string,
    view: string | null = null
  ): boolean {
    if (!this.user) {
      return false;
    }

    // ADMIN BYPASS: Admin users have unrestricted access to EVERYTHING
    if (this.user.role === "admin") {
      return true;
    }

    const dataPermissionKey = `${resource}.${action}`;
    if (this.user.role === "employee" && dataPermissionKey === "projects.create") {
      return true;
    }
    const legacyViewAccessKey = action === "access" ? `views.${resource}` : null;
    let viewPermissionKey: string | null = null;
    if (view) {
      viewPermissionKey = `${view}.${resource}.${action}`;
    }

    // If view context is specified, ONLY check view-specific permissions
    if (view && viewPermissionKey) {
      // First, ensure the user can access the view at all
      if (!this.canAccessView(view)) {
        return false;
      }

      // Check individual user view-specific override
      if (this.userPerms[viewPermissionKey] !== undefined) {
        if (this.userPerms[viewPermissionKey] === null) {
          // Inherit from data permission when explicitly null
          return this.hasFlatPermission(dataPermissionKey);
        }
        return this.userPerms[viewPermissionKey] === true;
      }

      // Check role-based view-specific override
      if (!this.hasAuthoritativePermissionPayload && this.rolePerms[viewPermissionKey] !== undefined) {
        if (this.rolePerms[viewPermissionKey] === null) {
          // Inherit from data permission when explicitly null
          return this.hasFlatPermission(dataPermissionKey);
        }
        return this.rolePerms[viewPermissionKey] === true;
      }

      // No view-specific override defined: fallback to data permission
      return this.hasFlatPermission(dataPermissionKey);
    }

    // No view context - check data permissions only
    // Check individual user permissions first
    if (this.userPerms[dataPermissionKey] !== undefined) {
      return this.userPerms[dataPermissionKey] === true;
    }
    if (legacyViewAccessKey && this.userPerms[legacyViewAccessKey] !== undefined) {
      return this.userPerms[legacyViewAccessKey] === true;
    }

    // Fall back to role-based permissions
    if (!this.hasAuthoritativePermissionPayload && this.rolePerms[dataPermissionKey] !== undefined) {
      return this.rolePerms[dataPermissionKey] === true;
    }
    if (!this.hasAuthoritativePermissionPayload && legacyViewAccessKey && this.rolePerms[legacyViewAccessKey] !== undefined) {
      return this.rolePerms[legacyViewAccessKey] === true;
    }

    if (action === "access" && this.hasProjectScopedViewAccess(resource)) {
      return true;
    }

    // Default to no permission
    return false;
  }

  /**
   * Check if user can access a specific view
   * @param viewName - The view name (e.g., 'dashboard', 'kanban')
   * @returns boolean - Whether user can access view
   */
  canAccessView(viewName: string): boolean {
    return this.hasPermission(viewName, "access");
  }

  /**
   * Get the required permission for a specific function
   * @param functionName - The function name from the mapping
   * @returns string - The required permission (e.g., 'tasks.create')
   */
  getPermissionForFunction(functionName: string): string {
    return FUNCTION_PERMISSION_MAP[functionName] || "";
  }

  /**
   * Validate if user can perform a specific operation
   * @param operation - The operation name from the mapping
   * @returns boolean - Whether user can perform operation
   */
  validateOperation(operation: string): boolean {
    const requiredPermission = this.getPermissionForFunction(operation);
    if (!requiredPermission) return false;

    const [resource, action] = requiredPermission.split(".");
    return this.hasPermission(resource, action);
  }

  /**
   * Get all permissions for current user
   * @returns PermissionStructure - Complete permission structure
   */
  getAllPermissions(): PermissionStructure {
    return {
      tasks: {
        create: this.hasPermission("tasks", "create"),
        read: this.hasPermission("tasks", "read"),
        update: this.hasPermission("tasks", "update"),
        delete: this.hasPermission("tasks", "delete"),
      },
      documents: {
        create: this.hasPermission("documents", "create"),
        read: this.hasPermission("documents", "read"),
        update: this.hasPermission("documents", "update"),
        delete: this.hasPermission("documents", "delete"),
      },
      calendar: {
        create: this.hasPermission("calendar", "create"),
        read: this.hasPermission("calendar", "read"),
        update: this.hasPermission("calendar", "update"),
        delete: this.hasPermission("calendar", "delete"),
      },
      users: {
        create: this.hasPermission("users", "create"),
        read: this.hasPermission("users", "read"),
        update: this.hasPermission("users", "update"),
        delete: this.hasPermission("users", "delete"),
      },
      agents: {
        execute: this.hasPermission("agents", "execute"),
      },
      feedback: {
        read: this.hasPermission("feedback", "read"),
        create: this.hasPermission("feedback", "create"),
        updateOwn: this.hasPermission("feedback", "updateOwn"),
        resolve: this.hasPermission("feedback", "resolve"),
        manage: this.hasPermission("feedback", "manage"),
        export: this.hasPermission("feedback", "export"),
      },
      views: {
        dashboard: this.hasPermission("dashboard", "access"),
        kanban: this.hasPermission("kanban", "access"),
        gantt: this.hasPermission("gantt", "access"),
        calendar: this.hasPermission("calendar", "access"),
        docs: this.hasPermission("docs", "access"),
        reports: this.hasPermission("reports", "access"),
        agentOffice: this.hasPermission("agent_office", "access"),
        upgrade: this.hasPermission("upgrade", "access"),
        settings: this.hasPermission("settings", "access"),
        admin: this.hasPermission("admin", "access"),
      },
      system: {
        import: this.hasPermission("system", "import"),
        export: this.hasPermission("system", "export"),
        history: this.hasPermission("system", "history"),
        search: this.hasPermission("system", "search"),
        customize: this.hasPermission("system", "customize"),
      },
    };
  }

  /**
   * Check if UI element should be visible/enabled
   * @param permission - The permission required
   * @returns boolean - Whether element should be visible
   */
  shouldShowUIElement(permission: string): boolean {
    const [resource, action] = permission.split(".");
    return this.hasPermission(resource, action);
  }

  /**
   * Get user role for permission debugging
   * @returns string - Current user role
   */
  getUserRole(): string {
    return this.user?.role || "anonymous";
  }

  /**
   * Check if user is admin
   * @returns boolean - Whether user is admin
   */
  isAdmin(): boolean {
    return this.user?.role === "admin";
  }

  /**
   * Get permission summary for debugging
   * @returns object - Permission summary
   */
  getPermissionSummary(): {
    user: string;
    role: string;
    permissions: PermissionStructure;
  } {
    return {
      user: this.user?.username || "anonymous",
      role: this.getUserRole(),
      permissions: this.getAllPermissions(),
    };
  }
}

/**
 * Default permission service instance
 * Will be updated when user changes
 */
export const permissionService: PermissionService = new PermissionService(null);

/**
 * Update the global permission service instance
 * @param user - Current user object
 */
export const updatePermissionService = (user: User | null): void => {
  permissionService.setUser(user);
};
